import { Pencil, Trash2, AlertTriangle, Link as LinkIcon } from 'lucide-preact'
import type { OnlineEventSpeakerViewDto } from '@/api/webs'
import { PersonAvatar } from '@/components/common/Avatar'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'

interface SpeakerUi extends OnlineEventSpeakerViewDto {}
interface SpeakerUiWithPic extends SpeakerUi { ProfilePicUrl?: string | null }

export function SpeakerCard({ speaker, onDelete, onEdit }: { speaker: SpeakerUi; onDelete?: (sp: SpeakerUi) => void; onEdit?: () => void }) {
  const { canEdit } = useWorkspaceCapabilities()
  const name = speaker.Name || 'Speaker'
  const title = speaker.Title || '\u2014'
  const company = speaker.CompanyName || '\u2014'
  const avatar = (speaker as SpeakerUiWithPic).ProfilePicUrl || null
  const bio = (speaker.Bio || '').trim()
  const speakerType = (speaker.SpeakerType || '').trim()
  const linkedIn = (speaker.LinkedInProfileUrl || '').trim()

  const missingFields: string[] = []
  if (!speaker.CompanyId && !speaker.CompanyName) missingFields.push('Company info')
  if (!speaker.Title) missingFields.push('Title')
  if (!bio) missingFields.push('Bio')

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
      <PersonAvatar
        fullName={name}
        profilePicUrl={avatar}
        size="lg"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                {linkedIn ? (
                  <a href={linkedIn} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
                    {name}
                    <LinkIcon className="w-3 h-3 text-primary-500" />
                  </a>
                ) : name}
              </div>
              {speakerType && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-200 dark:border-primary-800">{speakerType}</span>
              )}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{title}{company !== '\u2014' ? `, ${company}` : ''}</div>
            <div className="text-[11px] text-slate-400 dark:text-slate-500 break-all mt-0.5">{speaker.Email}</div>
            {missingFields.length > 0 && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Missing: {missingFields.join(', ')}
              </div>
            )}
          </div>
          <div className="flex items-start gap-1">
            {canEdit && onEdit && (
              <button type="button" onClick={onEdit} className="p-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Edit participant" title="Edit participant"><Pencil className="w-3 h-3" /></button>
            )}
            {canEdit && onDelete && (
              <button type="button" onClick={() => onDelete(speaker)} className="p-1 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20" aria-label="Remove participant" title="Remove participant"><Trash2 className="w-3 h-3" /></button>
            )}
          </div>
        </div>
        {bio && <p className="mt-2 text-xs text-slate-700 dark:text-slate-300 whitespace-pre-line line-clamp-5">{bio}</p>}
      </div>
    </div>
  )
}
